import { ReactNode, useMemo } from 'react'
import { matchPath } from 'react-router'
import { Navigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import cn from 'classnames'
import DwightLogo from '../../assets/images/dwight-text.svg?react'
// Reuse existing styles
import { ROUTES } from '../../constants/routes'
import { checkIsInIFrame } from '../../helpers/helpers'
import { useIsMobile } from '../../hooks/useIsMobile'
import Text from '../Common/Text'
import LoginBanner from '../LoginBanner'
import styles from './AuthLayout.module.scss'
import { PropsFromRedux } from './index'

const VALID_ROUTES = [
  ROUTES.LOGIN,
  ROUTES.LOGIN_ERROR,
  ROUTES.BBC_SIGN_SUCCESS,
  ROUTES.RESET_PASSWORD,
  ROUTES.PASSWORD_RECOVERY,
  ROUTES.CONFIRM_EMAIL,
  ROUTES.PASSWORD_SETUP,
  ROUTES.CLIENT_INTAKE,
  ROUTES.GLOBAL_ERROR,
]

export interface IProps extends PropsFromRedux {
  children: ReactNode
}

const AuthLayout = ({ children, isAuthenticated = false }: IProps) => {
  const location = useLocation()
  const pathname = location.pathname
  const routeNotFound = useMemo(() => {
    return !VALID_ROUTES.some((route) =>
      matchPath(
        {
          path: route,
        },
        pathname,
      ),
    )
  }, [pathname])

  const isBlankBackground = useMemo(
    () =>
      [ROUTES.GLOBAL_ERROR].some((route) => matchPath({ path: route }, pathname)) || routeNotFound,
    [pathname, routeNotFound],
  )

  const isClientIntake = useMemo(
    () =>
      matchPath(
        {
          path: ROUTES.CLIENT_INTAKE,
        },
        pathname,
      ) || routeNotFound,
    [pathname, routeNotFound],
  )

  const isMobileOrTablet = useIsMobile()

  const authBackgroundBanners = useMemo(() => {
    if (isClientIntake || isMobileOrTablet || checkIsInIFrame()) return null

    return isBlankBackground ? (
      <div className={styles.topLogoContainer}>
        <DwightLogo className={styles.dwightLogo} />
      </div>
    ) : (
      <LoginBanner />
    )
  }, [isBlankBackground, isClientIntake, isMobileOrTablet])

  // If authenticated, redirect to saved return URL or portfolio home (/)
  if (isAuthenticated) {
    const route = window.localStorage.getItem('return_url') || ROUTES.HOMEPAGE
    return <Navigate to={route} replace />
  }

  const isInIframe = checkIsInIFrame()
  return (
    <Box
      className={cn({
        [styles.container]: !isBlankBackground && !isInIframe,
        [styles.setupContainer]: isBlankBackground || isInIframe,
      })}
    >
      {children}

      {authBackgroundBanners}

      {!isClientIntake && (
        <>
          <a className={styles.footerLink} href="https://dwightfunding.com/privacy-policy/">
            <Text variant="button">Privacy Policy</Text>
          </a>
          <Text
            variant="bodyLg"
            tone="tertiary"
            strong
            component="div"
            className={cn(styles.copyright, {
              [styles.copyrightErrorPage]: isBlankBackground,
            })}
          >
            © Copyright {new Date().getFullYear()}
          </Text>
        </>
      )}
    </Box>
  )
}

export default AuthLayout
